import { useEffect, useState } from "react";

import SaveMethodSurface from "@/components/payments/save-method-surface";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui";

import { methodTypeFromRainforestEvent } from "@/lib/billing/method-type-from-rainforest-event";
import { stripWalletMethods } from "@/lib/patient-facing-allowed-methods";
import { useToast } from "@/lib/use-toast";

import { PaymentMethodType } from "@/types/enums/payment";
import { PaymentMethod } from "@/types/payment";
import { RainforestPaymentMethodConfig } from "@/types/rainforest";

import { createPaymentMethodConfig } from "@/api/provider/billing/payment-terminal";
import { useCreatePaymentMethodMutation } from "@/api/provider/user/patient/patient-mutation";

interface AddPaymentMethodModalProps {
  patientId: string;
  onSuccess: (data: PaymentMethod) => void;
  children: React.ReactNode;
  onMethodTypeChange?: (methodType: PaymentMethodType | null) => void;
  allowedMethods?: string;
}

const AddPaymentMethodModal = ({
  patientId,
  onSuccess,
  children,
  onMethodTypeChange,
  allowedMethods = "CARD,ACH",
}: AddPaymentMethodModalProps) => {
  // This modal always saves the method on file for future (merchant-initiated)
  // billing, so wallet tokens (Apple/Google Pay) — which are single-use and
  // can't back a recurring draw — are stripped even if a caller requests them.
  const rechargeableMethods = stripWalletMethods(allowedMethods);
  const { toast } = useToast();
  const [paymentMethodConfig, setPaymentMethodConfig] =
    useState<RainforestPaymentMethodConfig | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const createPaymentMethodMutation = useCreatePaymentMethodMutation();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // A config carries a single-use session key, so a response that arrives
    // after the dialog closed belongs to a session the next open won't use.
    let cancelled = false;
    createPaymentMethodConfig(patientId)
      .then((config) => {
        if (!cancelled && config) {
          setPaymentMethodConfig(config);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: "Payment Method Error",
            description:
              "Failed to load the payment form. Please close this window and try again.",
            variant: "error",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [patientId, isOpen]);

  const handleSuccess = async (paymentMethodId: string, event?: any) => {
    // `event` is Rainforest's; Stripe reports an id and nothing else. It is
    // read only for the optimistic row, never for what gets vaulted.
    const card = event?.detail?.[0]?.data?.card;
    const paymentMethod = await createPaymentMethodMutation.mutateAsync({
      patientId,
      paymentMethodId,
      preview: card ? { brand: card.brand, last4: card.last4 } : undefined,
    });
    setIsOpen(false);
    if (!paymentMethod) {
      toast({
        title: "Payment Method Error",
        description: "Failed to add payment method. Please try again.",
        variant: "error",
      });
      return;
    }
    toast({
      title: "Payment Method Added",
      description: "The payment method has been added successfully.",
      variant: "success",
    });
    onSuccess(paymentMethod);
    setPaymentMethodConfig(null);
  };

  const handleFailure = (error: string) => {
    toast({
      title: "Payment Method Error",
      description: error,
      variant: "error",
    });
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          // Drop the config so a reopen mounts the form against a fresh
          // session key rather than the one this open already claimed.
          setPaymentMethodConfig(null);
          onMethodTypeChange?.(null);
        }
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Payment Method</DialogTitle>
        </DialogHeader>

        <DialogBody>
          {paymentMethodConfig ? (
            <div className="mt-2">
              <p className="mb-4 text-body text-muted-foreground">
                Enter the payment method details below.
              </p>
              <SaveMethodSurface
                paymentMethodConfig={paymentMethodConfig}
                onSuccess={handleSuccess}
                onFailure={handleFailure}
                onValid={() => {}}
                onInvalid={() => {}}
                onMethodUpdated={(event) =>
                  onMethodTypeChange?.(methodTypeFromRainforestEvent(event))
                }
                hideButton={false}
                allowedMethods={rechargeableMethods}
              />
            </div>
          ) : (
            <div className="text-center">
              <p className="my-4 text-muted-foreground">
                Loading payment method config...
              </p>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default AddPaymentMethodModal;
